import { useState } from 'react';
import { PageHeader } from '@/components/Header/Page/PageHeader';
import { PageInfoHeading } from '@/components/Header/Page/PageInfoHeading';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useDeleteManyVehiculeChecks,
  useGetAllVehiculeChecks,
} from '@/hooks/vehicule-check.hooks';
import { VehiculeCheckTableView } from '../components/VehiculeCheckTableView';
import { TVehiculeCheckSchema } from '@/schemas/vehicule-check/vehicule-check.schema';
import { VehiculeCheckTableHeaders } from '../components/VehiculeCheckTableHeaders';
import { VehiculeCheckTableData } from '../components/VehiculeCheckTableData';
import { Trans, useTranslation } from 'react-i18next';
import { HeadingButton } from '@/components/Custom/Button/HeadingButton';
import { ImportIcon } from '@/components/SvgIcons/ImportIcon';
import { useGetVehiculeById } from '@/hooks/vehicule.hooks';
import Loader from '@/components/Loading/Loader';
import { useUserRole } from '@/hooks/auth.hooks';
import { useDeactivateField } from '@/hooks/deactivate-field.hooks';
import ConfirmationModal from '@/components/Custom/Modal/ConfirmationModal';
import { useQueryClient } from '@tanstack/react-query';
import {
  InspectionProgressStatusUpdatePayload,
  useSocket,
} from '@/hooks/socket.hooks';

export const VehiculeCheckListPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation('inspection');
  const { vehiculeId } = useParams();
  const { isModerator } = useUserRole();
  const [totalActive, setTotalActive] = useState<number>(0);

  /**
   * === Delete Data section ===
   */
  const {
    numberOfItemsToDeactivate,
    pendingDeactivate,
    handleDeactivate,
    onConfirmDeactivate,
  } = useDeactivateField<TVehiculeCheckSchema>({
    t,
    actionType: 'delete',
    useDeleteData: useDeleteManyVehiculeChecks,
  });
  /**
   * === END OF Delete Data section ===
   */

  const { data: vehicule, isLoading: isLoadingVehicule } =
    useGetVehiculeById(vehiculeId);

  const onGetTotalActive = (total: number) => {
    setTotalActive(total);
  };

  useSocket({
    onInspectionProgressStatusUpdate: (
      data: InspectionProgressStatusUpdatePayload
    ) => {
      queryClient.invalidateQueries({ queryKey: ['getAllVehiculeChecks'] });

      console.warn('inspectionProgressStatusUpdate', data);
    },
  });

  return (
    <>
      <PageHeader
        backToLink={vehiculeId ? -1 : undefined}
        title={t('common:inspections')}
        totalItems={totalActive}
        headingElement={
          <PageInfoHeading
            text={
              vehicule ? (
                <>
                  {t('common:inspectionsLinkedToTheVehicule')}{' '}
                  <span className="font-medium">
                    {t('common:registered').toLowerCase()}{' '}
                    {vehicule?.data?.licensePlateNumber}
                  </span>
                </>
              ) : (
                t('common:detailsRelatedToCurrentSection')
              )
            }
          />
        }
        actionListElement={
          !isModerator && (
            <>
              <HeadingButton
                text={t('common:importAList')}
                leftIcon={
                  <ImportIcon className="-mt-[1.5px] w-[15.5px] h-[17px] [&>path]:fill-light-inverse dark-mode:[&>path]:fill-gray-600" />
                }
                onClick={() => navigate('#')}
              />
              <HeadingButton
                text={t('common:requestAnInspection')}
                leftIcon={
                  <i className="-mt-[1.5px] ki-outline ki-plus-circle !text-[19.4px] text-light-inverse dark-mode:text-gray-600" />
                }
                onClick={() => navigate('/vehicule-check/create')}
              />
            </>
          )
        }
      />
      <div>
        <VehiculeCheckTableView<TVehiculeCheckSchema>
          t={t}
          useGetAllData={useGetAllVehiculeChecks}
          onGetTotalActive={onGetTotalActive}
          tableHeaders={VehiculeCheckTableHeaders}
          tableData={VehiculeCheckTableData}
          pendingDeactivate={pendingDeactivate}
          onDeactivate={handleDeactivate}
          receivedVehiculeId={vehiculeId}
        />
      </div>
      {isLoadingVehicule && <Loader />}
      <ConfirmationModal
        id="deactivation-confirm-modal"
        title={t('common:confirmation')}
        description={
          numberOfItemsToDeactivate > 1 ? (
            <Trans i18nKey="common:confirmDeleteItems" />
          ) : (
            t('common:confirmDeleteItem')
          )
        }
        onConfirmLabel={t('common:delete')}
        onConfirm={onConfirmDeactivate}
      />
    </>
  );
};
